/**
 * Pure, framework-agnostic helpers for the driver micro-app. Kept free of React
 * and DOM APIs so they can be unit tested with node:test.
 */

export function elapsedSeconds(startedAt, now = new Date()) {
  if (!startedAt) {
    return 0;
  }

  const start = startedAt instanceof Date ? startedAt : new Date(startedAt);
  const current = now instanceof Date ? now : new Date(now);
  const diffMs = current.getTime() - start.getTime();

  return diffMs > 0 ? Math.floor(diffMs / 1000) : 0;
}

export function formatDuration(totalSeconds) {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  const pad = (value) => String(value).padStart(2, "0");

  if (hrs > 0) {
    return `${hrs}:${pad(mins)}:${pad(secs)}`;
  }

  return `${pad(mins)}:${pad(secs)}`;
}

export function formatScheduledDateTime(value) {
  if (!value) {
    return "—";
  }

  const date = value instanceof Date ? value : new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}, ${date.toLocaleTimeString()}`;
}

function sanitisePhone(phone) {
  if (!phone) {
    return "";
  }

  // Keep a single leading +, drop everything else that is not a digit.
  const trimmed = String(phone).trim();
  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");

  return digits ? `${hasPlus ? "+" : ""}${digits}` : "";
}

export function buildTelLink(phone) {
  const sanitised = sanitisePhone(phone);

  return sanitised ? `tel:${sanitised}` : null;
}

export function buildWhatsAppLink(phone, message = "") {
  const sanitised = sanitisePhone(phone).replace(/^\+/, "");

  if (!sanitised) {
    return null;
  }

  const query = message ? `?text=${encodeURIComponent(message)}` : "";

  return `https://wa.me/${sanitised}${query}`;
}

export function buildMapLinks(address, coords) {
  const hasCoords = coords && Number.isFinite(coords.lat) && Number.isFinite(coords.lng);

  if (!hasCoords && !address) {
    return null;
  }

  const encodedAddress = address ? encodeURIComponent(address) : "";
  const latLng = hasCoords ? `${coords.lat},${coords.lng}` : "";

  return {
    waze: hasCoords
      ? `https://waze.com/ul?ll=${latLng}&navigate=yes`
      : `https://waze.com/ul?q=${encodedAddress}&navigate=yes`,
    googleMaps: hasCoords
      ? `https://www.google.com/maps/search/?api=1&query=${latLng}`
      : `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`,
    appleMaps: hasCoords
      ? `https://maps.apple.com/?ll=${latLng}&q=${encodedAddress || "Delivery"}`
      : `https://maps.apple.com/?q=${encodedAddress}`,
  };
}

export function formatStationName(station) {
  if (!station) {
    return "";
  }

  return [station.rw_name, station.rw_station_id ? `(${station.rw_station_id})` : ""]
    .filter(Boolean)
    .join(" ");
}

export function formatStationAddress(station) {
  if (!station) {
    return "";
  }

  return [station.rw_address1, station.rw_address2, station.rw_postal_code, station.rw_city]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(", ");
}

export function buildWatermarkLines(order, coords, now = new Date()) {
  const gps = coords && Number.isFinite(coords.lat) && Number.isFinite(coords.lng)
    ? `GPS: ${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`
    : "GPS: unavailable";

  return [
    order?.customer_address ?? "Delivery location",
    gps,
    (now instanceof Date ? now : new Date(now)).toISOString(),
  ];
}
